'use client';

import { auth } from '@ps/functions/firebase/auth';
import {
  getDownloadURL,
  ref,
  storage,
  uploadBytesResumable,
} from '@ps/functions/firebase/storage';
import invariant from 'tiny-invariant';

import type { LocalFileInfo } from '../types/FileInfo';
import type { UploadFileFn, UploadFileOptions } from '../types/UploadFileFn';

/**
 * Default {@link UploadFileFn} for files that live in Google Cloud
 * Storage (via Firebase Storage). Mirror of PopshopRN's
 * `appComponents/file-uploader/uploadFileAsync`.
 *
 * Uploads the file under `{directoryPath}/{uid}/{uuid}.{ext}` and
 * returns the long-lived `getDownloadURL` for the resulting object.
 *
 * Use this when your file type belongs in GCS (digital-product
 * downloads, app permissions blob, etc). For media-gallery / S3
 * assets (post images, channel-message attachments, etc) plug in an
 * `uploadMediaItem`-backed adapter instead.
 */
export const uploadFileAsync: UploadFileFn = async (
  file: LocalFileInfo,
  options?: UploadFileOptions,
): Promise<string> => {
  invariant(file.source === 'local', 'uploadFileAsync requires a local file');

  const ext = extractExtension(file.filename);
  invariant(ext, 'file must have a non-empty extension');

  const uid = auth().currentUser?.uid;
  invariant(uid, 'user must be signed in to upload files');

  // Normalize the directory path — strip a trailing slash so we can
  // join with a single separator below without doubling.
  const directoryPath = (options?.directoryPath ?? 'users').replace(/\/+$/, '');

  const objectPath = `${directoryPath}/${uid}/${crypto.randomUUID()}.${ext}`;

  const storageRef = ref(storage(), objectPath);

  // The browser File object IS a Blob, so we can pass it through to
  // `uploadBytesResumable` directly. The blob detour in PopshopRN
  // (`fetch(localUri).then(r => r.blob())`) was a React-Native-on-web
  // necessity — needed only as a fallback when the caller surfaced
  // an object URL without the originating File.
  let body: Blob | undefined = file.localFile;
  if (!body) {
    const response = await fetch(file.localUri);
    body = await response.blob();
  }
  invariant(body, 'uploadFileAsync: failed to resolve a Blob body');

  const task = uploadBytesResumable(storageRef, body, {
    contentType: file.type,
    contentDisposition: `attachment; filename*=utf-8''${encodeURIComponent(
      file.filename,
    )}`,
  });

  if (options?.onProgressChanged) {
    task.on('state_changed', snapshot => {
      const total = snapshot.totalBytes;
      const loaded = snapshot.bytesTransferred;
      const ratio = total > 0 ? Math.min(1, loaded / total) : 0;
      options.onProgressChanged?.(ratio);
    });
  }

  await task;
  return getDownloadURL(storageRef);
};

function extractExtension(filename: string): string | undefined {
  // Strip any query string the filename may carry (e.g. cached
  // copies of remote URLs the user dragged in). Then take the last
  // segment after the final `.`.
  const cleaned = filename.split('?')[0];
  const lastDot = cleaned.lastIndexOf('.');
  if (lastDot < 0 || lastDot === cleaned.length - 1) return undefined;
  return cleaned.slice(lastDot + 1).toLowerCase();
}
